import { Link } from 'react-router-dom';
import PublicShell from '@/components/layout/PublicShell';
import useSeo from '@/hooks/useSeo';
import { Check } from 'lucide-react';

const plans = [
    {
        name: 'Lite',
        price: '$14.900',
        period: '/mes',
        tagline: 'Para empezar a digitalizar tu gimnasio.',
        features: [
            '1 gimnasio',
            'Hasta 20 alumnos por gym',
            'Tarjetas de membresía',
            'Gestión de clases',
            'Consulta por DNI',
            'Soporte por email',
        ],
        cta: 'Empezar con Lite',
        popular: false,
    },
    {
        name: 'Pro',
        price: '$29.900',
        period: '/mes',
        tagline: 'Para gimnasios en crecimiento.',
        features: [
            'Hasta 3 gimnasios',
            'Hasta 50 alumnos por gym',
            'Todo lo del plan Lite',
            'Pagos y facturación',
            'Soporte prioritario',
        ],
        cta: 'Empezar con Pro',
        popular: true,
    },
    {
        name: 'Enterprise',
        price: '$59.900',
        period: '/mes',
        tagline: 'Para cadenas y operaciones a gran escala.',
        features: [
            'Gimnasios ilimitados',
            'Alumnos ilimitados',
            'Todo lo del plan Pro',
            'Integraciones API',
            'Soporte dedicado 24/7',
            'Onboarding personalizado',
        ],
        cta: 'Empezar con Enterprise',
        popular: false,
    },
];

export default function Pricing() {
    useSeo('Precios', 'Planes y precios de GYMS.ar: Lite, Pro y Enterprise. Elegí el plan ideal para tu gimnasio.');

    return (
        <PublicShell>
            <section className="pt-40 pb-24 px-6 lg:px-10 bg-black">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-3xl mb-16">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-px h-5 bg-white/60" />
                            <span className="micro-cap text-white/60 tracking-[0.2em]">Precios</span>
                        </div>
                        <h1 className="text-white mb-4">Planes</h1>
                        <p className="text-white/60 text-base max-w-xl">
                            Elegí el plan que mejor se adapte a tu gimnasio. Todos incluyen acceso a la plataforma,
                            sin costos ocultos.
                        </p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
                        {plans.map((plan) => (
                            <div
                                key={plan.name}
                                className={`flex flex-col border rounded-lg p-8 ${
                                    plan.popular ? 'border-white' : 'border-hairline-dark bg-[#0a0a0a]'
                                }`}
                            >
                                {plan.popular && (
                                    <span className="micro-cap text-white/70 mb-6 tracking-[0.2em]">Más popular</span>
                                )}
                                <h2 className="text-white mb-1" style={{ fontSize: '1.5rem' }}>
                                    {plan.name}
                                </h2>
                                <p className="text-sm text-white/50 mb-6">{plan.tagline}</p>
                                <div className="flex items-baseline gap-2 mb-8">
                                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                                    {plan.period && <span className="text-sm text-white/50">{plan.period}</span>}
                                </div>
                                <ul className="space-y-3 flex-1">
                                    {plan.features.map((f) => (
                                        <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                                            <Check className="h-4 w-4 text-white shrink-0" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/register"
                                    className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-xs uppercase font-bold tracking-[0.15em] transition-colors ${
                                        plan.popular
                                            ? 'bg-white text-black hover:bg-white/90'
                                            : 'border border-white/60 text-white hover:bg-white hover:text-black'
                                    }`}
                                >
                                    {plan.cta}
                                </Link>
                            </div>
                        ))}
                    </div>

                    {/* Custom integration / self-hosted */}
                    <div className="mt-16 max-w-5xl mx-auto">
                        <div className="border border-hairline-dark rounded-lg p-8 md:p-12 bg-[#0a0a0a]">
                            <div className="flex flex-col md:flex-row md:items-center gap-8">
                                <div className="flex-1">
                                    <span className="micro-cap text-white/60 tracking-[0.2em] mb-4 inline-block">
                                        Integraciones a medida
                                    </span>
                                    <h2 className="text-white mb-4" style={{ fontSize: '1.75rem' }}>
                                        Servicio self-hosted y datos personalizados
                                    </h2>
                                    <p className="text-white/70 text-base leading-relaxed max-w-2xl">
                                        ¿Necesitás integrar GYMS.ar con tus propios sistemas, alojarlo en tu
                                        infraestructura o manejar datos a medida? Ofrecemos implementaciones
                                        personalizadas para grandes organizaciones y cadenas de gimnasios.
                                    </p>
                                </div>
                                <div className="shrink-0">
                                    <a
                                        href="mailto:hola@gyms.ar"
                                        className="inline-flex items-center justify-center rounded-full px-8 py-4 text-xs uppercase font-bold tracking-[0.15em] border border-white/60 text-white hover:bg-white hover:text-black transition-colors"
                                    >
                                        Contactanos a hola@gyms.ar
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </PublicShell>
    );
}
